/* ============================================================
   Somber — accessibility menu (snippets/accessibility-somber.liquid)
   Each choice is a class on <html> (a11y-contrast, a11y-text-1 ...),
   saved to localStorage and re-applied early by the inline script in
   layout/theme.liquid so pages don't flash the default look first.
   Other scripts can check window.somberA11y.still() and listen for
   the `somber:a11y-change` event (slideshows pause on "still").
   ============================================================ */

(() => {
  const KEY = 'somber-a11y';
  const OPTIONS = ['contrast', 'colourblind', 'font', 'links', 'still'];
  const FONT_URL = 'https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:ital,wght@0,400;0,700;1,400&display=swap';
  const root = document.documentElement;

  const read = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || {};
    } catch (e) {
      return {};
    }
  };

  const write = (prefs) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(prefs));
    } catch (e) {}
  };

  const loadFont = () => {
    if (document.getElementById('a11y-font-link')) return;
    const link = document.createElement('link');
    link.id = 'a11y-font-link';
    link.rel = 'stylesheet';
    link.href = FONT_URL;
    document.head.appendChild(link);
  };

  const apply = (prefs) => {
    OPTIONS.forEach((name) => root.classList.toggle(`a11y-${name}`, !!prefs[name]));
    [1, 2].forEach((size) => root.classList.toggle(`a11y-text-${size}`, prefs.size === size));
    if (prefs.font) loadFont();
    // Background videos (e.g. the hero) are autoplaying motion too.
    document.querySelectorAll('video[autoplay]').forEach((video) => {
      if (prefs.still) video.pause();
      else if (video.paused) video.play().catch(() => {});
    });
    document.dispatchEvent(new CustomEvent('somber:a11y-change', { detail: { ...prefs } }));
  };

  window.somberA11y = {
    still: () => root.classList.contains('a11y-still'),
  };

  apply(read());

  // Shopify Inbox's chat button also lives bottom-right. Inbox renders <shopify-chat> (full-screen,
  // pointer-events: none) with its activator button in an open shadow root; its size and position
  // follow the Inbox "Activator" settings, so measure it and sit the menu just above it.
  const GAP = 12;
  let chatButton = null;
  const resizeWatch = new ResizeObserver(() => placeAboveChat());

  const findChatButton = (host) =>
    [...(host.shadowRoot?.querySelectorAll('button') || [])].find((button) => {
      const r = button.getBoundingClientRect();
      return r.width > 0 && r.bottom > innerHeight - 240 && r.right > innerWidth - 400;
    });

  function placeAboveChat() {
    const host = document.querySelector('shopify-chat');
    if (!host) {
      root.classList.remove('a11y-has-chat');
      return;
    }
    const button = findChatButton(host);
    // While the chat window is open the activator can hide; keep the last position.
    if (!button) return;
    if (button !== chatButton) {
      if (chatButton) resizeWatch.unobserve(chatButton);
      chatButton = button;
      resizeWatch.observe(button);
    }
    const r = button.getBoundingClientRect();
    root.style.setProperty('--a11y-chat-bottom', `${Math.round(innerHeight - r.top + GAP)}px`);
    root.style.setProperty('--a11y-chat-right', `${Math.round(innerWidth - r.right)}px`);
    root.classList.add('a11y-has-chat');
  }

  let watchedShadow = null;
  const watchChat = () => {
    const host = document.querySelector('shopify-chat');
    if (host?.shadowRoot && host.shadowRoot !== watchedShadow) {
      watchedShadow = host.shadowRoot;
      new MutationObserver(placeAboveChat).observe(watchedShadow, { childList: true, subtree: true, attributes: true });
    }
    placeAboveChat();
  };
  watchChat();
  new MutationObserver(watchChat).observe(document.body, { childList: true });
  window.addEventListener('resize', placeAboveChat);
  // The chat draws its button a moment after it loads.
  [500, 1500, 3000, 6000].forEach((ms) => setTimeout(watchChat, ms));

  if (customElements.get('a11y-menu')) return;

  customElements.define(
    'a11y-menu',
    class A11yMenu extends HTMLElement {
      connectedCallback() {
        this.toggleButton = this.querySelector('[data-a11y-toggle]');
        this.panel = this.querySelector('.a11y-menu__panel');
        this.status = this.querySelector('[data-a11y-status]');
        this.prefs = read();

        this.toggleButton.addEventListener('click', () => (this.panel.hidden ? this.open() : this.close()));
        this.querySelector('[data-a11y-close]').addEventListener('click', () => this.close(true));

        this.querySelectorAll('[data-a11y-option]').forEach((button) =>
          button.addEventListener('click', () => {
            const name = button.dataset.a11yOption;
            this.update({ [name]: !this.prefs[name] });
            this.announce(`${button.querySelector('.a11y-menu__option-name').textContent} ${this.prefs[name] ? 'on' : 'off'}`);
          })
        );

        this.querySelectorAll('[data-a11y-size]').forEach((button) =>
          button.addEventListener('click', () => {
            this.update({ size: parseInt(button.dataset.a11ySize, 10) });
            this.announce(button.querySelector('.visually-hidden').textContent);
          })
        );

        this.querySelector('[data-a11y-reset]').addEventListener('click', () => {
          this.prefs = {};
          write(this.prefs);
          apply(this.prefs);
          this.sync();
          this.announce('Display settings reset');
        });

        this.onKeydown = (event) => {
          if (event.key === 'Escape' && !this.panel.hidden) this.close(true);
        };
        this.onOutside = (event) => {
          if (!this.panel.hidden && !this.contains(event.target)) this.close();
        };
        document.addEventListener('keydown', this.onKeydown);
        document.addEventListener('click', this.onOutside);

        this.sync();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('click', this.onOutside);
      }

      open() {
        this.panel.hidden = false;
        this.toggleButton.setAttribute('aria-expanded', 'true');
        this.panel.querySelector('button').focus();
      }

      close(returnFocus) {
        this.panel.hidden = true;
        this.toggleButton.setAttribute('aria-expanded', 'false');
        if (returnFocus) this.toggleButton.focus();
      }

      update(change) {
        this.prefs = { ...this.prefs, ...change };
        if (!this.prefs.size) delete this.prefs.size;
        OPTIONS.forEach((name) => {
          if (!this.prefs[name]) delete this.prefs[name];
        });
        write(this.prefs);
        apply(this.prefs);
        this.sync();
      }

      sync() {
        this.querySelectorAll('[data-a11y-option]').forEach((button) =>
          button.setAttribute('aria-pressed', this.prefs[button.dataset.a11yOption] ? 'true' : 'false')
        );
        const size = this.prefs.size || 0;
        this.querySelectorAll('[data-a11y-size]').forEach((button) =>
          button.setAttribute('aria-pressed', parseInt(button.dataset.a11ySize, 10) === size ? 'true' : 'false')
        );
        this.toggleButton.classList.toggle('is-active', Object.keys(this.prefs).length > 0);
      }

      announce(message) {
        this.status.textContent = '';
        requestAnimationFrame(() => (this.status.textContent = message));
      }
    }
  );
})();
